/**
 * Export Control Wrapper
 * Wraps the standalone export control app with proper API configuration
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, Tabs, Tab, Alert, Container, Breadcrumbs, Link, Paper } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import SecurityIcon from '@mui/icons-material/Security';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';
import LicenseCheck from './src/components/LicenseCheck';
import ExtractComponents from './src/components/ExtractComponents';
import HistoryTab from './src/components/HistoryTab';
import { useECTranslation } from './src/i18n';
import { brand } from '../../theme';
import './src/App.css';
import './src/index.css';

type PageType = 'license' | 'extract' | 'history';

// Set the API base URL for export control
// Empty string = same origin (works for both localhost and network access via nginx proxy)
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    // @ts-ignore
    return window.REACT_APP_API_URL || '';
  }
  return '';
};

const API_BASE_URL = getApiBaseUrl();
const EXPORT_CONTROL_API = `${API_BASE_URL}/api/v1/export-control`;

// Store original fetch
const originalFetch = window.fetch;

const ExportControlWrapper: React.FC = () => {
  const { t } = useECTranslation();
  const navigate = useNavigate();
  // The standalone "Licence check" tab is hidden from the UI; the licence check
  // still runs inline during a dual-use check. Land on "Extract components".
  const [currentPage, setCurrentPage] = useState<PageType>('extract');

  useEffect(() => {
    // Intercept fetch calls and redirect to the correct API
    window.fetch = function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      let url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      
      // If the URL doesn't start with http/https, it's a relative URL from export control
      if (!url.startsWith('http')) {
        // Remove leading slash if present
        url = url.startsWith('/') ? url.slice(1) : url;
        
        // Map export control endpoints to the integrated API
        url = `${EXPORT_CONTROL_API}/${url}`;
      }
      
      // Add authentication token from localStorage
      const authState = localStorage.getItem('auth-storage');
      if (authState) {
        try {
          const parsed = JSON.parse(authState);
          const accessToken = parsed.state?.token?.access_token;
          if (accessToken) {
            init = init || {};
            init.headers = {
              ...init.headers,
              'Authorization': `Bearer ${accessToken}`,
            };
          }
        } catch (e) {
          console.error('Failed to parse auth state:', e);
        }
      }
      
      return originalFetch(url, init);
    };

    // Cleanup on unmount - restore original fetch
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  return (
    <Box sx={{ height: '100%', bgcolor: 'grey.50', overflow: 'auto' }}>
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Breadcrumbs — same style as the Tariff Classifier module */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/process')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 0.5 }} fontSize="small" />
            {t('wrapper.title')}
          </Typography>
        </Breadcrumbs>

        {/* Page heading */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('wrapper.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('wrapper.subtitle')}
          </Typography>
        </Box>

        {/* Nav bar — Paper-wrapped MUI Tabs (Tariff Classifier style).
            "Licence check" tab hidden; it runs inline in the dual-use flow. */}
        <Paper elevation={1} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={currentPage}
            onChange={(_e: React.SyntheticEvent, v: PageType) => setCurrentPage(v)}
            sx={{
              borderBottom: 1,
              borderColor: brand.borderSubtle,
              px: 2,
              '& .MuiTabs-indicator': { backgroundColor: brand.orange },
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
              '& .Mui-selected': { color: `${brand.orange} !important` },
            }}
          >
            <Tab value="extract" icon={<FactCheckIcon />} label={t('wrapper.tab.extract')} iconPosition="start" />
            <Tab value="history" icon={<HistoryIcon />} label={t('wrapper.tab.history')} iconPosition="start" />
          </Tabs>

          {/* Page content — inside the same container (Paper) as the nav tabs */}
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {currentPage === 'license' && <LicenseCheck />}
            {currentPage === 'extract' && <ExtractComponents />}
            {currentPage === 'history' && <HistoryTab />}
          </Box>
        </Paper>

        {/* How it works */}
        <Alert severity="info">
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {t('wrapper.how.title')}
          </Typography>
          <Typography variant="caption" component="div">
            {t('wrapper.how.dualUse')}
          </Typography>
          <Typography variant="caption" component="div" sx={{ pl: 2 }}>
            {t('wrapper.how.extract')}
          </Typography>
          <Typography variant="caption" component="div" sx={{ pl: 2 }}>
            {t('wrapper.how.compliance')}
          </Typography>
          <Typography variant="caption" component="div">
            {t('wrapper.how.history')}
          </Typography>
        </Alert>
      </Container>
    </Box>
  );
};

export default ExportControlWrapper;
